<script lang="ts">
    import type { ApiService } from "../services";
    import { userState } from "../modules/store";
    import Tradename from "../components/Tradename.svelte";
    import { getContext } from "svelte";
    import type { UserProcessNavigator } from "../modules/UserProcessNavigator";

    const api: ApiService = getContext("api-service");
    const nav: UserProcessNavigator = getContext("navigator");

    nav.navigateToDefaultForCurrentState();

    let email: string;
    let password: string;
    let otpRequestPending: boolean = false;
    let otpRequested = false;
    let otpReqeustSuccess = false;
    let loginSuccess: boolean = false;
    let loginAttempted: boolean = false;
    $: missingFields = !(email && password);
    $: otpRequestFailure = otpRequested && !otpReqeustSuccess;

    function loginDataChanged() {
        loginAttempted = false;
    }
    async function requestOtp(): Promise<any> {
        otpRequestPending = true;
        const response = await api.RequestOtpAsync(email);
        otpRequestPending = false;
        otpReqeustSuccess = response.success;
        otpRequested = true;
        return;
    }
    async function login(e): Promise<any> {
        const loginResponse = await api.LoginAsync(email, password);
        loginSuccess = loginResponse.success;
        loginAttempted = true;
        if (loginResponse.success) {
            const assessmentResponse = await api.GetAssessmentAsync();
            userState.update((s) => {
                s.isLoggedIn = api.isLoggedIn;
                s.accessMode = loginResponse.data.accessMode;
                if (assessmentResponse.success) {
                    s.assessment = assessmentResponse.data;
                }
                return s;
            });
            nav.next();
        }
    }
</script>

<div class="login-container">
    <div class="login-card">
        <h2 class="login-title">Login to <Tradename /></h2>
        <form class="login-form">
            <div class="input-group">
                <input
                    type="text"
                    name="email"
                    placeholder="Email Address"
                    class="login-input"
                    id="lastemailName"
                    class:error={loginAttempted && missingFields && !email}
                    bind:value={email}
                    on:change={loginDataChanged}
                />
            </div>
            <div class="input-group">
                <input
                    type="password"
                    name="password"
                    id="password"
                    class="login-input"
                    placeholder="One-time Password"
                    class:hidden={!(otpRequested && otpReqeustSuccess)}
                    class:error={loginAttempted && missingFields && !password}
                    bind:value={password}
                    on:change={loginDataChanged}
                />
            </div>
        </form>
        
        <button
            on:click={login}
            disabled={missingFields}
            class:hidden={!(otpRequested && otpReqeustSuccess)}
            class="submit-button login-button"
        >
            Login to <Tradename />
        </button>
        
        <div class="error-message">
            {#if !loginSuccess && loginAttempted}
                The email address or One-time Password did not match.
            {:else if otpRequestFailure}
                Unable to confirm email from authorized participant list. Please
                contact your plan sponsor or send an email to
                info@mylongevityquest.com
            {:else}
                &nbsp;
            {/if}
        </div>
        
        <button class="submit-button otp-button" on:click={requestOtp} disabled={!email}>
            {#if !otpRequested}
                {#if !otpRequestPending}
                    Request One-time Password
                {:else}
                    Sending One-time Password...
                {/if}
            {:else}
                Resend One-time Password
            {/if}
        </button>
    </div>
</div>

<style>
.login-container {
    display: flex;
    justify-content: center;
    align-items: center;
    min-height: 100vh;
    padding: 20px;
}

.login-card {
    background: white;
    padding: 40px;
    border-radius: 20px;
    box-shadow: 0 4px 15px rgba(0,0,0,0.07);
    width: 100%;
    max-width: 400px;
    text-align: center;
}

.login-title {
    color: #2c3e50;
    font-size: 2rem;
    font-weight: 700;
    margin-bottom: 30px;
    text-align: center;
}

.login-form {
    margin-bottom: 30px;
}

.input-group {
    margin-bottom: 20px;
}

.login-input {
    width: 100%;
    padding: 16px 20px;
    border: 2px solid #e9ecef;
    border-radius: 12px;
    font-size: 1rem;
    transition: all 0.3s ease;
    background: #f8f9fa;
    color: #2c3e50;
    box-sizing: border-box;
}

.login-input:focus {
    outline: none;
    border-color: #ffd966;
    background: white;
    box-shadow: 0 0 0 3px rgba(255, 217, 102, 0.1);
}

.login-input::placeholder {
    color: #6c757d;
}

.login-input.error {
    border-color: #dc3545;
    background: #fff5f5;
}

.login-input.hidden {
    display: none;
}

.submit-button {
    display: inline-block;
    background: #ffd966;
    color: #2c3e50;
    padding: 16px 40px;
    border-radius: 30px;
    font-size: 1.2rem;
    font-weight: 600;
    text-decoration: none;
    transition: all 0.3s ease;
    box-shadow: 0 4px 15px rgba(255, 217, 102, 0.3);
    border: none;
    cursor: pointer;
    width: 100%;
    margin-bottom: 15px;
}

.submit-button:hover:not(:disabled) {
    transform: translateY(-2px);
    box-shadow: 0 6px 20px rgba(255, 217, 102, 0.4);
    background: #ffe066;
}

.submit-button:disabled {
    opacity: 0.6;
    cursor: not-allowed;
    transform: none;
}

.submit-button.hidden {
    display: none;
}

.login-button {
    margin-bottom: 20px;
}

.otp-button {
    margin-top: 10px;
}

.error-message {
    color: #dc3545;
    font-size: 0.9rem;
    margin-bottom: 20px;
    min-height: 20px;
    line-height: 1.4;
}

@media (max-width: 768px) {
    .login-container {
        padding: 10px;
    }
    
    .login-card {
        padding: 30px 20px;
    }
    
    .login-title {
        font-size: 1.8rem;
    }
    
    .submit-button {
        font-size: 1.1rem;
        padding: 14px 30px;
    }
}
</style>
